import { useState } from "react";
import { Loader2, CalendarCheck, Wrench, IndianRupee, CheckCircle2, Circle, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/Field";
import { api, fmtINR } from "@/lib/api";
import { ReceiptButton } from "@/components/ReceiptButton";
import { useLang } from "@/context/LanguageContext";
import { CertificateButton } from "@/components/Certificate";
import { ChampionCertificates } from "@/components/ChampionCertificate";
import { ParentProjectPhotos } from "@/components/ProjectPhotos";
import { ParentAlerts } from "@/components/ParentAlerts";
import { ParentClassHistory } from "@/components/ClassHistory";
import { ParentSmart } from "@/components/smart/ParentSmart";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
    <Icon className="w-6 h-6 text-[#FF2E63]" />
    <div className="text-xs uppercase tracking-widest text-slate-500 mt-3">{label}</div>
    <div className="text-3xl font-extrabold text-[#0A192F] font-['Outfit']">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Report = ({ r, creds }) => {
  const { L } = useLang();
  const pct = r.sessions ? Math.round((r.present / r.sessions) * 100) : 0;
  const done = r.projects.filter((p) => p.status === "completed").length;
  return (
    <div className="mt-10 space-y-6" data-testid="progress-report">
      <div className="bg-[#F0F4F8] text-[#0A192F] rounded-2xl p-6 flex flex-wrap justify-between gap-4">
        <div>
          <div className="text-xs text-[#007A8A] font-bold uppercase tracking-widest">{L("Student", "छात्र")}</div>
          <div className="text-2xl font-extrabold" data-testid="progress-student-name">{r.student_name}</div>
          <div className="text-slate-600 text-sm">{L(`Class ${r.class_grade}`, `कक्षा ${r.class_grade}`)} · {r.school_name}</div>
        </div>
        <div className="sm:text-right">
          <div className="text-xs text-slate-500">{L("Program", "कार्यक्रम")}</div>
          <div className="font-bold">{r.program} · {r.plan}</div>
          <div className="text-xs text-slate-500 mt-1">{L("Receipt", "रसीद")} {r.receipt_no}</div>
        </div>
      </div>
      <ParentAlerts creds={creds} />
      {r.program_completed && <CertificateButton r={r} />}
      <ChampionCertificates champions={r.champions} photo={r.showcase?.[0]?.photos?.[0]} schoolLabel={`Partner School${(r.showcase?.[0]?.city || r.city) ? `, ${r.showcase?.[0]?.city || r.city}` : ""}`} />
      <ParentProjectPhotos items={r.showcase} />
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat icon={CalendarCheck} label={L("Attendance", "उपस्थिति")} value={`${pct}%`} sub={L(`${r.present} of ${r.sessions} sessions`, `${r.sessions} में से ${r.present} सत्र`)} testid="progress-attendance" />
        <Stat icon={Wrench} label={L("Projects completed", "पूरे प्रोजेक्ट")} value={done} sub={L(`${r.projects.length - done} in progress`, `${r.projects.length - done} जारी`)} testid="progress-projects" />
        <Stat icon={IndianRupee} label={L("Learn & Earn earnings", "सीखें और कमाएँ कमाई")} value={fmtINR(r.earnings_total)} testid="progress-earnings" />
      </div>
      {r.billing && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 grid sm:grid-cols-4 gap-3 text-sm" data-testid="progress-billing">
          <div><div className="text-xs text-slate-500">{L("Annual fee", "वार्षिक फ़ीस")}</div><b>{fmtINR(r.billing.fee_inr)}</b></div>
          <div><div className="text-xs text-slate-500">{L("Coupon", "कूपन")}</div><b data-testid="progress-coupon">{r.billing.coupon_code ? `${r.billing.coupon_code} (−${r.billing.discount_pct}%)` : "—"}</b></div>
          <div><div className="text-xs text-slate-500">{L("Paid", "भुगतान")}</div><b>{fmtINR(r.billing.amount_inr)}</b></div>
          <div><div className="text-xs text-slate-500">{L("Student Login ID", "छात्र लॉगिन ID")}</div><b data-testid="progress-login-id">{r.billing.login_id || "—"}</b></div>
          {r.referral && <p className="sm:col-span-4 text-sm text-[#0A192F]" data-testid="progress-referral">{L("Friends who joined through your link", "आपके लिंक से जुड़े दोस्त")}: <b>{r.referral.friends || 0}</b>{r.referral.badge && <span className="ml-2 inline-block bg-[#FFD166] text-[#0A192F] text-xs font-bold rounded-full px-2.5 py-0.5" data-testid="progress-referral-badge">{r.referral.badge}</span>}<span className="block text-xs text-slate-500">{L("Share link", "शेयर लिंक")}: {window.location.origin}/enroll?ref={r.referral.ref_code}</span></p>}
          <p className="sm:col-span-4 text-[11px] text-[#B42318]">{L("Kits are not included in the fee.", "फ़ीस में किट शामिल नहीं हैं।")}</p>
        </div>
      )}
      <ParentClassHistory creds={creds} />
      <ParentSmart creds={creds} />
      {r.teacher_note && (
        <div className="bg-[#FF2E63]/10 border border-[#FF2E63]/40 rounded-2xl p-5 flex gap-3" data-testid="progress-note">
          <MessageSquare className="w-5 h-5 text-[#FF2E63] shrink-0 mt-0.5" />
          <div><div className="font-bold text-[#0A192F]">{L("Mentor's note", "मेंटर की टिप्पणी")}</div><p className="text-[#0A192F] text-sm mt-1">{r.teacher_note}</p></div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-[#0A192F] mb-3">{L("Projects", "प्रोजेक्ट्स")}</h3>
          {r.projects.length === 0 ? <p className="text-sm text-slate-500">{L("No projects recorded yet.", "अभी कोई प्रोजेक्ट दर्ज नहीं।")}</p> : (
            <ul className="space-y-2" data-testid="progress-project-list">
              {r.projects.map((p, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  {p.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-500 mt-0.5" />}
                  <span className="flex-1">{p.title}</span><span className="text-xs text-slate-500">{p.date}</span>
                </li>
              ))}
            </ul>
          )}
          <h3 className="font-bold text-[#0A192F] mt-6 mb-3">{L("Earnings", "कमाई")}</h3>
          {r.earnings.length === 0 ? <p className="text-sm text-slate-500">{L("No earnings yet — keep building!", "अभी कोई कमाई नहीं — बनाते रहें!")}</p> : (
            <ul className="space-y-2 text-sm" data-testid="progress-earning-list">
              {r.earnings.map((x, i) => <li key={i} className="flex justify-between"><span>{x.note || "—"} <span className="text-xs text-slate-500">{x.date}</span></span><b>{fmtINR(x.amount_inr)}</b></li>)}
            </ul>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-[#0A192F] mb-3">{L("Attendance record", "उपस्थिति रिकॉर्ड")}</h3>
          {r.attendance.length === 0 ? <p className="text-sm text-slate-500">{L("Classes haven't started yet.", "कक्षाएँ अभी शुरू नहीं हुई हैं।")}</p> : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="progress-attendance-list">
              {r.attendance.map((a) => (
                <div key={a.date} className={`text-center text-xs rounded-lg py-2 font-semibold ${a.present ? "bg-emerald-500/15 text-emerald-700" : "bg-red-500/15 text-red-600"}`}>
                  {new Date(a.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}<br />{a.present ? L("Present", "उपस्थित") : L("Absent", "अनुपस्थित")}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ParentProgress() {
  const { L } = useLang();
  const [f, setF] = useState({ receipt_no: "", contact: "" });
  const [r, setR] = useState(null);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr(""); setR(null);
    try {
      const { data } = await api.post("/progress/lookup", f);
      setR(data);
      setSent({ ...f });
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : L("Please check the details.", "कृपया जानकारी जाँचें।"));
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#E8F0FE] min-h-[75vh]" data-testid="progress-page">
      <div className="max-w-5xl mx-auto px-5 py-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2E63]">{L("Parent Portal", "अभिभावक पोर्टल")}</span>
        <h1 className="text-4xl font-extrabold text-[#0A192F] mt-2 leading-tight">{L("Attendance & Progress", "उपस्थिति और प्रगति")}</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">{L("See your child's attendance, projects and Learn & Earn income. Use the receipt number from your enrollment email plus your phone or email.", "अपने बच्चे की उपस्थिति, प्रोजेक्ट्स और कमाई देखें। दाखिला ईमेल की रसीद संख्या और अपना फ़ोन या ईमेल डालें।")}</p>
        <form onSubmit={submit} className="mt-8 bg-white p-6 rounded-2xl border border-slate-200 grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end" data-testid="progress-form">
          <Field id="receipt_no" label={L("Receipt number", "रसीद संख्या")} required placeholder="e.g. 3F9A12BC" value={f.receipt_no} onChange={(e) => setF({ ...f, receipt_no: e.target.value })} />
          <Field id="parent_contact" label={L("Parent phone or email", "अभिभावक फ़ोन या ईमेल")} required value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} />
          <Button type="submit" disabled={loading} className="rs-cta h-11 rounded-full px-8" data-testid="progress-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : L("View report", "रिपोर्ट देखें")}
          </Button>
          {err && <p className="sm:col-span-3 text-sm text-red-600" data-testid="progress-error">{err}</p>}
        </form>
        {r && sent && <div className="mt-6 flex justify-end"><ReceiptButton path="/receipts/enrollment" body={sent} testid="progress-receipt-btn" /></div>}
        {r && sent && <Report r={r} creds={sent} />}
      </div>
    </div>
  );
}
